"use client";

import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import Cookies from "js-cookie";
import { ShopifyCart, createCart, getCart, addToCart, updateCartLines, removeFromCart, applyDiscountToCart, clearDiscountsFromCart } from "@/lib/shopify-api";
import { getGiftHandlesForProduct } from "@/lib/shopify-gifts";
import { usePostHog } from "@/hooks/usePostHog";
// Resolve the live campaign code at call time — a module-load constant is how
// the storefront kept serving a dead summer code after its window closed.
import { isCampaignActive, getCampaignCode } from "@/lib/campaign";

interface CartContextType {
  cart: ShopifyCart | null;
  loading: boolean;
  error: string | null;
  addItem: (variantId: string, quantity?: number, variantTitle?: string) => Promise<void>;
  updateItem: (lineId: string, quantity: number) => Promise<void>;
  removeItem: (lineId: string) => Promise<void>;
  clearError: () => void;
  clearCart: () => Promise<void>;
  applyDiscount: (discountCode: string) => Promise<boolean>;
  clearDiscounts: () => Promise<boolean>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_COOKIE_NAME = "shopify_cart_id";

export function CartProvider({ children }: { children: ReactNode }) {
  const [cart, setCart] = useState<ShopifyCart | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { track } = usePostHog();

  // Initialize cart on mount
  useEffect(() => {
    // Add a small delay to ensure environment variables are loaded
    const timer = setTimeout(() => {
      initializeCart();
    }, 150);

    return () => clearTimeout(timer);
  }, []);

  const initializeCart = async () => {
    try {
      setLoading(true);
      const existingCartId = Cookies.get(CART_COOKIE_NAME);
      
      if (existingCartId) {
        // Try to fetch existing cart
        const existingCart = await getCart(existingCartId);
        if (existingCart) {
          setCart(existingCart);
          // Auto-apply the campaign discount to existing cart if promo is active and cart has items
          if (isCampaignActive() && existingCart.lines.edges.length > 0) {
            try {
              const discountedCart = await applyDiscountToCart(existingCart.id, [getCampaignCode()!]);
              if (discountedCart) setCart(discountedCart);
            } catch (e) {
              console.warn('Failed to auto-apply the campaign discount to existing cart:', e);
            }
          }
        } else {
          // Cart doesn't exist, create a new one
          await createNewCart();
        }
      } else {
        // No existing cart, create a new one
        await createNewCart();
      }
    } catch (err) {
      setError('Failed to initialize cart');
      console.error('Cart initialization error:', err);
    } finally {
      setLoading(false);
    }
  };

  const createNewCart = async () => {
    try {
      const newCartId = await createCart();
      Cookies.set(CART_COOKIE_NAME, newCartId, { expires: 30 }); // 30 days
      const newCart = await getCart(newCartId);
      setCart(newCart);
    } catch (err) {
      throw new Error('Failed to create cart');
    }
  };

  const addItem = async (variantId: string, quantity: number = 1, variantTitle?: string) => {
    if (!cart) {
      setError('Cart not initialized');
      return;
    }

    try {
      setError(null);
      const updatedCart = await addToCart(cart.id, variantId, quantity);
      
      if (updatedCart) {
        
        // Track add to cart event
        const addedLine = updatedCart.lines.edges.find(edge => edge.node.merchandise.id === variantId);
        if (addedLine) {
          track('add_to_cart', {
            product_id: addedLine.node.merchandise.product.id,
            product_handle: addedLine.node.merchandise.product.handle,
            product_title: addedLine.node.merchandise.product.title,
            variant_id: variantId,
            variant_title: variantTitle || addedLine.node.merchandise.title,
            quantity: quantity,
            price: parseFloat(addedLine.node.merchandise.price.amount),
            currency: addedLine.node.merchandise.price.currencyCode,
            cart_total: parseFloat(updatedCart.cost.totalAmount.amount),
            cart_quantity: updatedCart.totalQuantity,
            timestamp: new Date().toISOString()
          });
        }
        
        setCart(updatedCart);

        // Auto-apply the campaign discount discount to cart when promo is active
        if (isCampaignActive()) {
          try {
            const discountedCart = await applyDiscountToCart(updatedCart.id, [getCampaignCode()!]);
            if (discountedCart) setCart(discountedCart);
          } catch (e) {
            console.warn('Failed to auto-apply the campaign discount to cart:', e);
          }
        }

      } else {
        // Cart is invalid, create a new one and retry
        const newCartId = await createCart();
        Cookies.set(CART_COOKIE_NAME, newCartId, { expires: 30 });
        const newCart = await getCart(newCartId);
        
        if (newCart) {
          setCart(newCart);
          // Retry adding item to new cart
          const retryCart = await addToCart(newCartId, variantId, quantity);
          if (retryCart) {
            setCart(retryCart);
            if (isCampaignActive()) {
              try {
                const discountedCart = await applyDiscountToCart(retryCart.id, [getCampaignCode()!]);
                if (discountedCart) setCart(discountedCart);
              } catch {}
            }
          }
        }
      }
    } catch (err) {
      setError('Failed to add item to cart');
      console.error('Add to cart error:', err);
    }
  };

  const updateItem = async (lineId: string, quantity: number) => {
    if (!cart) {
      setError('Cart not initialized');
      return;
    }

    try {
      setError(null);
      
      // If quantity is 0, remove the item and its associated gifts
      if (quantity === 0) {
        await removeItem(lineId);
        return;
      }
      
      const updatedCart = await updateCartLines(cart.id, [{ id: lineId, quantity }]);
      if (updatedCart) {
        // Track cart update event
        const updatedLine = updatedCart.lines.edges.find(edge => edge.node.id === lineId);
        if (updatedLine) {
          track('cart_item_updated', {
            product_id: updatedLine.node.merchandise.product.id,
            product_title: updatedLine.node.merchandise.product.title,
            variant_id: updatedLine.node.merchandise.id,
            old_quantity: cart.lines.edges.find(edge => edge.node.id === lineId)?.node.quantity || 0,
            new_quantity: quantity,
            price: parseFloat(updatedLine.node.merchandise.price.amount),
            cart_total: parseFloat(updatedCart.cost.totalAmount.amount),
            cart_quantity: updatedCart.totalQuantity
          });
        }
        setCart(updatedCart);
      }
    } catch (err) {
      setError('Failed to update cart item');
      console.error('Update cart error:', err);
    }
  };

  const removeItem = async (lineId: string) => {
    if (!cart) {
      setError('Cart not initialized');
      return;
    }

    try {
      setError(null);
      
      // Find the item being removed for tracking
      const lineToRemove = cart.lines.edges.find(edge => edge.node.id === lineId);
      
      // Track remove from cart event
      if (lineToRemove) {
        track('remove_from_cart', {
          product_id: lineToRemove.node.merchandise.product.id,
          product_title: lineToRemove.node.merchandise.product.title,
          variant_id: lineToRemove.node.merchandise.id,
          variant_title: lineToRemove.node.merchandise.title,
          quantity: lineToRemove.node.quantity,
          price: parseFloat(lineToRemove.node.merchandise.price.amount),
          cart_total_before: parseFloat(cart.cost.totalAmount.amount),
          cart_quantity_before: cart.totalQuantity
        });
      }
      
      // If removing a main product, also remove its associated gifts in the cart UI
      let lineIdsToRemove = [lineId];
      if (lineToRemove) {
        const isMainProduct = parseFloat(lineToRemove.node.cost.totalAmount.amount) > 0;
        if (isMainProduct) {
          try {
            const handles = getGiftHandlesForProduct(lineToRemove.node.merchandise.product.title);
            if (handles.length > 0) {
              const giftLines = cart.lines.edges.filter(edge =>
                parseFloat(edge.node.cost.totalAmount.amount) === 0 &&
                handles.includes(edge.node.merchandise.product.handle)
              );
              if (giftLines.length > 0) {
                lineIdsToRemove = [lineId, ...giftLines.map(edge => edge.node.id)];
              }
            }
          } catch (e) {
            console.warn('Failed to compute gifts to remove with main product:', e);
          }
        }
      }

      // Remove the main item and any associated gifts
      const updatedCart = await removeFromCart(cart.id, lineIdsToRemove);
      
      if (updatedCart) {
        setCart(updatedCart);
      }
    } catch (err) {
      setError('Failed to remove item from cart');
      console.error('Remove from cart error:', err);
    }
  };

  const clearError = () => setError(null);

  const clearCart = async () => {
    try {
      // Track cart clearing before clearing
      if (cart && cart.lines.edges.length > 0) {
        track('cart_cleared', {
          cart_total: parseFloat(cart.cost.totalAmount.amount),
          cart_quantity: cart.totalQuantity,
          items_count: cart.lines.edges.length,
          currency: cart.cost.totalAmount.currencyCode
        });
      }

      // Create a completely new cart
      const newCartId = await createCart();
      Cookies.set(CART_COOKIE_NAME, newCartId, { expires: 30 });
      const newCart = await getCart(newCartId);
      if (newCart) {
        setCart(newCart);
      }
    } catch (err) {
      console.error('Error clearing cart:', err);
    }
  };

  const applyDiscount = async (discountCode: string): Promise<boolean> => {
    if (!cart) {
      setError('Cart not initialized');
      return false;
    }

    try {
      setError(null);
      const updatedCart = await applyDiscountToCart(cart.id, [discountCode]);
      if (updatedCart) {
        setCart(updatedCart);
        return true;
      }
      return false;
    } catch (err: any) {
      setError('Failed to apply discount: ' + err.message);
      console.error('Apply discount error:', err);
      return false;
    }
  };

  const clearDiscounts = async (): Promise<boolean> => {
    if (!cart) {
      setError('Cart not initialized');
      return false;
    }

    try {
      setError(null);
      const updatedCart = await clearDiscountsFromCart(cart.id);
      if (updatedCart) {
        setCart(updatedCart);
        return true;
      }
      return false;
    } catch (err: any) {
      setError('Failed to clear discounts: ' + err.message);
      console.error('Clear discounts error:', err);
      return false;
    }
  };

  const value: CartContextType = {
    cart,
    loading,
    error,
    addItem,
    updateItem,
    removeItem,
    clearError,
    clearCart,
    applyDiscount,
    clearDiscounts,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
